import {
  AuthenticationResult,
  EventMessage,
  EventType,
  PublicClientApplication,
} from '@azure/msal-browser';
import { msalConfig } from '../config/authConfig';

// Coffee Run MSAL Instance - Original Implementation
export const msalInstance = new PublicClientApplication(msalConfig);

// Normalize and export test auth flag (supports 'true' in any case)
export const enableTestAuth =
  (import.meta.env.VITE_TEST_AUTH || '').toLowerCase() === 'true';
console.log(
  '[MSAL] VITE_TEST_AUTH raw value =',
  import.meta.env.VITE_TEST_AUTH
);
if (enableTestAuth) {
  // Minimal mock account structure matching MSAL expectations
  const mockAccount: any = {
    homeAccountId: 'test-account-id',
    localAccountId: 'test-local-id',
    environment: 'login.microsoftonline.com',
    tenantId: 'test-tenant',
    username: 'testuser@coffee.run',
    name: 'Test User',
  };

  // Attach mock accounts collection
  (msalInstance as any)._mockAllAccounts = [mockAccount];

  // Monkey patch getAllAccounts to always return our mock set in test mode
  const originalGetAllAccounts = msalInstance.getAllAccounts.bind(msalInstance);
  (msalInstance as any).getAllAccounts = () => {
    const real = originalGetAllAccounts();
    return real && real.length > 0
      ? real
      : (msalInstance as any)._mockAllAccounts;
  };

  // Provide mock login flows that simulate success without network
  const mockLoginResult: Partial<AuthenticationResult> = {
    account: mockAccount,
  };
  (msalInstance as any).loginPopup = async () => {
    console.log('[MSAL][TEST] loginPopup mock invoked');
    msalInstance.setActiveAccount(mockAccount);
    window.dispatchEvent(new Event('storage'));
    return mockLoginResult;
  };
  (msalInstance as any).loginRedirect = async () => {
    console.log('[MSAL][TEST] loginRedirect mock invoked');
    msalInstance.setActiveAccount(mockAccount);
    window.dispatchEvent(new Event('storage'));
    return mockLoginResult;
  };

  // Immediately set active account
  msalInstance.setActiveAccount(mockAccount);
  console.log('[MSAL][TEST] Injected mock test account');
}

// Initialize MSAL and handle redirect promise
msalInstance
  .initialize()
  .then(() => {
    console.log('[MSAL] Instance initialized');

    // Handle redirect promise - this is crucial for redirect flow
    msalInstance
      .handleRedirectPromise()
      .then(response => {
        console.log('[MSAL] handleRedirectPromise response:', response);

        if (response) {
          // Handle successful redirect
          console.log('[MSAL] Redirect response received:', response);
          msalInstance.setActiveAccount(response.account);

          // Force a page refresh to update React state
          setTimeout(() => {
            console.log(
              '[MSAL] Triggering state update after successful login'
            );
            window.dispatchEvent(new Event('storage')); // Trigger React re-render
          }, 100);
        } else {
          console.log('[MSAL] No redirect response found');
        }

        // Account selection logic is app dependent. Adjust as needed for different use cases.
        const accounts = msalInstance.getAllAccounts();
        console.log('[MSAL] All accounts:', accounts);

        if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
          console.log('[MSAL] Setting first account as active:', accounts[0]);
          msalInstance.setActiveAccount(accounts[0]);
        }
      })
      .catch(error => {
        console.error('[MSAL] Error handling redirect promise:', error);
      });

    msalInstance.addEventCallback((event: EventMessage) => {
      console.log('[MSAL] Event callback:', event.eventType, event);

      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
        console.log('[MSAL] Login success event, set active account:', account);
      }

      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        msalInstance.setActiveAccount(null);
        console.log('[MSAL] Logout success event');
      }

      if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        console.log('[MSAL] Token acquired successfully');
      }

      if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        console.error('[MSAL] Token acquisition failed:', event.error);
      }
    });
  })
  .catch(error => {
    console.error('[MSAL] Failed to initialize:', error);
  });

// Helper function to get access token
export const getAccessToken = async (scopes: string[] = ['User.Read']) => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw new Error(
      'No active account! Verify a user has been signed in and setActiveAccount has been called.'
    );
  }

  const response = await msalInstance.acquireTokenSilent({
    scopes,
    account,
  });
  return response.accessToken;
};

// Helper function to sign out
export const signOut = () => {
  const logoutRequest = {
    account: msalInstance.getActiveAccount(),
    postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
  };
  // Use redirect instead of popup for more reliable logout
  msalInstance.logoutRedirect(logoutRequest);
};
